/**
 * 606 Meta5 AI Trading - Calendar Tab
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { useTrading } from '../../lib/trading-i18n';
import { fetchCalendar } from '../../lib/trading-api';
import Badge from '../../components/trading/Badge';

const CalendarTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCalendar = useCallback(async () => {
    try {
      const data = await fetchCalendar(7);
      setEvents(data.data || []);
    } catch (err) {
      console.error('Failed to load calendar:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const formatDate = (isoString) => {
    try {
      return new Date(isoString).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '--';
    }
  };

  const formatTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleTimeString(language === 'ar' ? 'ar-AE' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '--';
    }
  };

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = formatDate(event.event_time);
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Calendar className="h-5 w-5 text-[#d4af37]" />
        {t.calendar}
      </h3>

      {events.length === 0 ? (
        <div className="text-center py-12 text-[#71717A]">{t.noEvents}</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEvents).map(([date, dayEvents]) => (
            <motion.div
              key={date}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h4 className="text-[#d4af37] font-medium mb-3 pb-2 border-b border-white/10">{date}</h4>
              <div className="space-y-2">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white/[0.02] rounded-lg border border-white/10 p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-[#71717A] text-sm w-16">{formatTime(event.event_time)}</span>
                      <Badge variant="gold">{event.currency}</Badge>
                      <Badge variant={event.impact}>{event.impact}</Badge>
                      <span className="text-white">{event.title}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {event.forecast && (
                        <div>
                          <span className="text-[#71717A]">{t.forecast}: </span>
                          <span className="text-white">{event.forecast}</span>
                        </div>
                      )}
                      {event.previous && (
                        <div>
                          <span className="text-[#71717A]">{t.previous}: </span>
                          <span className="text-white">{event.previous}</span>
                        </div>
                      )}
                      {event.actual && (
                        <div>
                          <span className="text-[#71717A]">{t.actual}: </span>
                          <span className="text-emerald-400 font-medium">{event.actual}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalendarTab;
